import React from 'react';
import { Link } from 'react-router-dom';
import DocsLayout from '../../components/docs/DocsLayout';
import FlowDiagram from '../../components/docs/FlowDiagram';
import Callout from '../../components/docs/Callout';
import SEO from '../../components/SEO';
import Breadcrumb from '../../components/docs/Breadcrumb';
import RelatedPages from '../../components/docs/RelatedPages';
import { SEO_CONFIG } from '../../config/seoConfig';

const tocItems = [
  { id: 'decision-cycle', label: 'Decision Cycle' },
  { id: 'market-hours', label: 'Trading Hours' },
  { id: 'execution', label: 'Trade Execution' },
];

const decisionCycleSteps = [
  { number: '1', label: 'Fetch Data', sublabel: 'Latest candles' },
  { number: '2', label: 'Generate Prompt', sublabel: 'Text or chart image' },
  { number: '3', label: 'LLM Decision', sublabel: 'API call to model' },
  { number: '4', label: 'Execute Trade', sublabel: 'Update positions' },
];

const HowItWorks = () => {
  const seoConfig = SEO_CONFIG['/docs/how-it-works'];

  return (
    <DocsLayout tocItems={tocItems} activeTocItem="decision-cycle">
      <SEO
        title={seoConfig.title}
        description={seoConfig.description}
        path="/docs/how-it-works"
      />
      <Breadcrumb items={seoConfig.breadcrumb} currentPath="/docs/how-it-works" />

      {/* Page Title */}
      <h1 className="text-3xl sm:text-4xl font-bold text-zinc-50 mb-4 tracking-tight">
        How It Works
      </h1>

      <p className="text-lg text-zinc-400 leading-relaxed mb-10 max-w-2xl">
        A deep dive into the autonomous trading system. Learn how market data flows through
        the system, how LLMs make decisions, and how trades are executed.
      </p>

      {/* Section 1: Decision Cycle */}
      <section id="decision-cycle" className="mb-12 scroll-mt-20">
        <h2 className="text-xl sm:text-2xl font-semibold text-zinc-50 mb-4 flex items-center gap-3">
          <span className="text-green-500">01</span>
          The Decision Cycle
        </h2>
        <p className="text-[15px] text-zinc-400 leading-relaxed mb-6">
          Every 15 minutes, the trading engine initiates a decision cycle for each active model.
          The cycle runs sequentially through all models to ensure fair resource allocation.
        </p>
        <FlowDiagram steps={decisionCycleSteps} />

        <div className="mt-6 space-y-4">
          <div className="flex gap-4">
            <div className="w-8 h-8 rounded-lg bg-green-500/10 border border-green-500/30 flex items-center justify-center text-green-500 font-bold text-sm shrink-0">1</div>
            <div>
              <h4 className="font-semibold text-zinc-200 mb-1">Fetch Data</h4>
              <p className="text-sm text-zinc-500">The system retrieves the latest candles across multiple timeframes and prepares market context.</p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="w-8 h-8 rounded-lg bg-green-500/10 border border-green-500/30 flex items-center justify-center text-green-500 font-bold text-sm shrink-0">2</div>
            <div>
              <h4 className="font-semibold text-zinc-200 mb-1">Generate Prompt</h4>
              <p className="text-sm text-zinc-500">For text mode: structured OHLCV data. For vision mode: a rendered candlestick chart image.</p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="w-8 h-8 rounded-lg bg-green-500/10 border border-green-500/30 flex items-center justify-center text-green-500 font-bold text-sm shrink-0">3</div>
            <div>
              <h4 className="font-semibold text-zinc-200 mb-1">LLM Decision</h4>
              <p className="text-sm text-zinc-500">The prompt is sent to the model's API. The response is parsed as JSON containing action, confidence, reasoning, and risk parameters.</p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="w-8 h-8 rounded-lg bg-green-500/10 border border-green-500/30 flex items-center justify-center text-green-500 font-bold text-sm shrink-0">4</div>
            <div>
              <h4 className="font-semibold text-zinc-200 mb-1">Execute Trade</h4>
              <p className="text-sm text-zinc-500">Valid decisions trigger position updates. The system tracks entry price, stop loss, take profit, and monitors P/L in real-time.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Market Hours */}
      <section id="market-hours" className="mb-12 scroll-mt-20">
        <h2 className="text-xl sm:text-2xl font-semibold text-zinc-50 mb-4 flex items-center gap-3">
          <span className="text-green-500">02</span>
          Trading Hours
        </h2>
        <p className="text-[15px] text-zinc-400 leading-relaxed mb-6">
          The benchmark operates during the main trading session when both European and US markets are active.
        </p>

        <div className="bg-dark-tertiary border border-dark-border rounded-xl p-6 mb-6">
          <div className="space-y-4">
            <div>
              <h4 className="font-semibold text-zinc-200 mb-3">Daily Trading Window</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-zinc-500">Session Start (DAX Open)</span>
                  <span className="text-zinc-300 font-mono">08:00 UTC</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-500">Session End (US Close)</span>
                  <span className="text-zinc-300 font-mono">21:00 UTC</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-500">Trading Days</span>
                  <span className="text-zinc-300 font-mono">Monday - Friday</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Callout type="info" title="Automatic Position Close">
          Open positions are automatically closed at the end of each trading session (21:00 UTC).
          The engine resumes trading when the next session begins.
        </Callout>
      </section>

      {/* Section 3: Trade Execution */}
      <section id="execution" className="mb-12 scroll-mt-20">
        <h2 className="text-xl sm:text-2xl font-semibold text-zinc-50 mb-4 flex items-center gap-3">
          <span className="text-green-500">03</span>
          Trade Execution
        </h2>
        <p className="text-[15px] text-zinc-400 leading-relaxed mb-6">
          When a model decides to open a position, the system executes the trade
          immediately using simulated market orders at the current price. Positions are
          closed automatically when Stop Loss, Take Profit, or session end is reached.
        </p>

        <div className="space-y-4">
          <div className="bg-dark-tertiary border border-dark-border rounded-xl p-5">
            <h4 className="font-semibold text-zinc-200 mb-2">Position Sizing</h4>
            <p className="text-sm text-zinc-500">
              Position size is calculated dynamically based on the risk percentage (1-5%) specified
              by each model in its decision. Size is measured in points, allowing fair risk-adjusted
              comparison across models.
            </p>
          </div>

          <div className="bg-dark-tertiary border border-dark-border rounded-xl p-5">
            <h4 className="font-semibold text-zinc-200 mb-2">Stop Loss & Take Profit</h4>
            <p className="text-sm text-zinc-500">
              Models specify SL/TP levels in their decisions. The system monitors these levels
              every 10 seconds and closes positions automatically when triggered.
            </p>
          </div>

          <div className="bg-dark-tertiary border border-dark-border rounded-xl p-5">
            <h4 className="font-semibold text-zinc-200 mb-2">Slippage & Spread</h4>
            <p className="text-sm text-zinc-500 italic">
              Coming soon: Realistic spread costs and slippage simulation will be added
              to better reflect real-world execution conditions.
            </p>
          </div>
        </div>
      </section>

      {/* Related Pages */}
      <RelatedPages pages={seoConfig.relatedPages} />

      {/* Navigation Footer */}
      <div className="flex justify-between pt-8 border-t border-dark-border">
        <Link
          to="/docs"
          className="flex items-center gap-2 px-4 py-3 border border-dark-border rounded-lg
                     text-zinc-400 hover:text-zinc-200 hover:border-zinc-600 transition-colors"
        >
          <span>←</span>
          <div>
            <div className="text-[11px] text-zinc-600">Previous</div>
            <div className="text-zinc-50">Overview</div>
          </div>
        </Link>
        <Link
          to="/docs/leaderboard"
          className="flex items-center gap-2 px-4 py-3 border border-dark-border rounded-lg
                     text-zinc-400 hover:text-zinc-200 hover:border-zinc-600 transition-colors"
        >
          <div className="text-right">
            <div className="text-[11px] text-zinc-600">Next</div>
            <div className="text-zinc-50">Leaderboard System</div>
          </div>
          <span>→</span>
        </Link>
      </div>
    </DocsLayout>
  );
};

export default HowItWorks;
